import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { conversationsApi } from '../services/api';
import type { Conversation } from '../types';
import { Avatar } from '../components/ui/Avatar';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { formatRelativeTime } from '../utils/helpers';
import { useAuthStore } from '../stores/authStore';

// Calca ScreenChatList de screens-chat.jsx (Claude Design)
export const Messages: React.FC = () => {
  const { user } = useAuthStore();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    conversationsApi.list().then((res) => {
      setConversations(res.data.data ?? res.data);
      setLoading(false);
    });
  }, []);

  const getOtherUser = (conv: Conversation) => (conv.client_id === user?.id ? conv.worker : conv.client);

  const visible = useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.toLowerCase();
    return conversations.filter((c) => getOtherUser(c)?.full_name.toLowerCase().includes(q) || c.job?.title.toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, search]);

  return (
    <div className="bg-surface-dim min-h-full">
      <div className="max-w-xl mx-auto">
        <div className="px-4 pt-4 pb-3">
          <h1 className="text-xl font-bold text-onSurface">Mensajes</h1>
        </div>

        <div className="px-4 pb-3">
          <div className="h-11 rounded-full bg-surface border border-outlineVarWarm flex items-center px-3.5">
            <MaterialIcon name="search" size={18} className="text-onSurface-mute mr-2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar conversaciones"
              className="flex-1 bg-transparent outline-none text-sm text-onSurface placeholder-onSurface-mute"
            />
          </div>
        </div>

        {loading ? (
          <div className="px-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-12 h-12 rounded-full bg-surface" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-24 bg-surface rounded" />
                  <div className="h-2 w-full bg-surface rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <MaterialIcon name="forum" size={44} className="text-onSurface-mute mx-auto mb-3" />
            <p className="text-onSurface-mute text-sm">No tenés conversaciones todavía</p>
          </div>
        ) : (
          <div className="bg-surface border-t border-outlineVarWarm">
            {visible.map((conv) => {
              const other = getOtherUser(conv);
              return (
                <Link
                  key={conv.id}
                  to={`/messages/${conv.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-surface-dim transition-colors border-b border-outlineVarWarm last:border-0"
                >
                  <Avatar src={other?.avatar} name={other?.full_name} size="lg" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`text-[15px] truncate ${conv.unread_count ? 'font-bold text-onSurface' : 'font-semibold text-onSurface'}`}>
                        {other?.full_name}
                      </span>
                      <span className={`text-[11px] shrink-0 ${conv.unread_count ? 'text-primary-700 font-bold' : 'text-onSurface-mute'}`}>
                        {formatRelativeTime(conv.last_message_at || conv.created_at)}
                      </span>
                    </div>
                    {conv.job && (
                      <p className="text-[11px] text-onSurface-mute truncate mt-0.5 inline-flex items-center gap-1">
                        <MaterialIcon name="work" size={11} className="text-primary-700" />
                        {conv.job.title}
                      </p>
                    )}
                    {!!conv.unread_count && (
                      <div className="flex justify-end mt-0.5">
                        <span className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-primary-500 text-white text-[11px] font-bold flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
